import { ReactNode } from 'react';

// YouTube Service Types
export interface YouTubeVideo {
  id: string;
  title: string;
  description: string;
  thumbnailUrl: string;
  duration: number;
  viewCount: string;
  publishedAt: string;
  channelTitle: string;
}

export interface YouTubeSearchParams {
  query?: string;
  maxResults?: number;
  pageToken?: string;
}

export interface YouTubeSearchResponse {
  videos: YouTubeVideo[];
  nextPageToken?: string;
  prevPageToken?: string;
  totalResults: number;
}

export interface YouTubeApiVideoItem {
  id: string;
  snippet: {
    title: string;
    description: string;
    thumbnails: {
      default?: { url: string };
      medium?: { url: string };
      high?: { url: string };
    };
    publishedAt: string;
    channelTitle: string;
  };
  statistics: {
    viewCount: string;
  };
  contentDetails: {
    duration: string;
  };
}

export interface YouTubeApiPlaylistItem {
  id:
    | {
        videoId?: string;
      }
    | string;
  snippet: {
    resourceId?: {
      videoId: string;
    };
  };
}

export interface YouTubeApiChannelItem {
  id: string;
  contentDetails: {
    relatedPlaylists: {
      uploads: string;
    };
  };
}

export interface YouTubeApiResponse<T> {
  result: {
    items: T[];
    nextPageToken?: string;
    prevPageToken?: string;
    pageInfo?: {
      totalResults: number;
    };
  };
}

export interface YouTubeApiRequestOptions {
  part: string[];
  maxResults?: number;
  type?: string;
  q?: string;
  pageToken?: string;
  playlistId?: string;
  id?: string[];
  mine?: boolean;
}

// Google API Loader Types
export interface GoogleApiLoaderState {
  isGapiLoaded: boolean;
  loadPromise: Promise<void> | null;
}

// Google Authentication Types
export enum GoogleService {
  YouTube = 'youtube',
  Picker = 'picker',
}

export interface GoogleApiLoaderConfig {
  apiKey: string;
  clientId: string;
  scopes: string[];
  serviceScopes?: Record<GoogleService, string[]>;
}
export interface GoogleAuthToken {
  access_token: string;
  token_type: string;
  expires_in: number;
  scope: string;
  service?: GoogleService;
  issued_at?: number;
}

export interface GoogleAuthContextType {
  isLoading: boolean;
  getServiceToken: (service: GoogleService) => GoogleAuthToken | undefined;
  authorize: (service: GoogleService, usePopup?: boolean) => Promise<void>;
  authError: string | undefined;
  setAuthError: (error: string | undefined) => void;
  logout: (service: GoogleService) => void;
  isAuthenticated: (service: GoogleService) => boolean;
}

export interface GoogleAuthProviderProps {
  children: ReactNode;
}

export interface GoogleConfig {
  apiKey: string;
  clientId: string;
  scopes: string[];
  serviceScopes?: Record<GoogleService, string[]>;
}

export class GoogleAuthError extends Error {
  public readonly isUserFriendly: boolean;

  constructor(message: string, isUserFriendly = false) {
    super(message);
    this.name = 'GoogleAuthError';
    this.isUserFriendly = isUserFriendly;
  }
}
